import moment from 'moment'
import { saveAs } from 'file-saver'

import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'
import { Loading } from 'element-ui'
import crypto from 'crypto-js'

import { IS_PRODUCTION } from '@/const'
import { NormalizedResponse } from '@/types/axios/public-json-structure'

export function getToday() {
  return moment(new Date()).format('yyyy-MM-DD')
}

/**
 *
 * @param {number} day
 * @returns {date}
 */
export function getLastDay(day: number) {
  return moment().subtract(day, 'days').format('YYYY-MM-DD')
}

// 数据类型判断
export function dataType(o: any) {
  if (o === null) return 'Null'
  if (o === undefined) return 'Undefined'
  return Object.prototype.toString.call(o).slice(8, -1)
}
/**
 * 对原始请求参数进行请求，删除原始请求参数中的空值及空字符串
 * @param params 原始请求参数
 * @returns 经过处理的请求参数
 */
export function handlerQueryParams<T extends Record<string | number, any>>(
  params: T
): Partial<T> {
  const reg = [null, undefined, '']
  /**
   * 如果params类型是一个纯对象
   */
  if (dataType(params) === 'Object') {
    const formParams = {} as Partial<T>
    for (const key in params) {
      if (!reg.includes(params[key])) {
        formParams[key] = params[key]
      }
    }
    return formParams
  }
  return params
}

// 获得周范围
export function getWeekRange(date = undefined) {
  return [
    moment(date)
      .week(moment(date).week())
      .startOf('isoWeek')
      .format('yyyy-MM-DD'),
    moment(date).week(moment(date).week()).endOf('isoWeek').format('yyyy-MM-DD')
  ]
}

// 获得月范围
export function getMonthRange(date = undefined) {
  return [
    moment(date)
      .month(moment(date).month())
      .startOf('month')
      .format('yyyy-MM-DD'),
    moment(date).month(moment(date).month()).endOf('month').format('yyyy-MM-DD')
  ]
}

export type Dictionary<T> = {
  [k: string]: T
}

//todo 替换 any 为具体的 filter type
export const getEmptyFilter = (
  filterConfig: Dictionary<any>[],
  field = 'prop'
): Dictionary<any> => {
  const filter: Dictionary<any> = {}
  filterConfig.forEach((item) => {
    filter[item[field]] = null
  })

  return filter
}

export const getDefaultFilter = <T>(
  filterConfig: Dictionary<any>[],
  field = 'prop',
  defaultVal?: any
): T => {
  const filter = {} as T
  filterConfig.forEach((item) => {
    const v = item['defaultVal'] ?? defaultVal ?? null
    const k = item[field]
    Object.defineProperty(filter, k, {
      value: v,
      writable: true,
      enumerable: true,
      configurable: true
    })
  })

  return filter
}

// 使用 source 对 target 上的同名属性的值进行更新, source 上有但是 target 上没有的属性不会额外增加至 target
export function updateObject<T>(target: T, source: Dictionary<any>): T {
  for (const k in target) {
    if (k in source) {
      target[k] = source[k]
    }
  }
  return target
}

export function isInViewport(el: HTMLElement): boolean {
  const height =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight
  const top = el.getBoundingClientRect() && el.getBoundingClientRect().top
  return top < height
}

export const freeze = Object.freeze

let uid = 1
export function guid(prefix = '', suffix = ''): string {
  uid++
  return prefix + uid + suffix
}
/**
 * 10000 => "10,000"
 * @param {number} num
 */
export function toThousand(num: string | number) {
  return (+num || 0)
    .toString()
    .replace(/^-?\d+/g, (m) => m.replace(/(?=(?!\b)(\d{3})+$)/g, ','))
}

/**
 * 解析 element date-picker 为 range 类型时的结果, 指定为 { prop1: startDate, prop2: endDate }
 * @param range date-picker 组件 value, e.g. ['2020-01-02', '2020-01-03']
 * @param prop1 要指定的第 1 个 prop 字段名, e.g. 'startDate'
 * @param prop2 要指定的第 2 个 prop 字段名, e.g. 'endDate'
 * @return
 *  {
 *    startDate: '2020-01-02',
 *    endDate: '2020-01-03',
 *  }
 */
export function parseDateRange(
  range: string[] | null | undefined,
  prop1: string,
  prop2: string
): { [k: string]: string } {
  if (!range) {
    return {}
  } else {
    return {
      [prop1]: range[0],
      [prop2]: range[1]
    }
  }
}

export function downloadFile(
  res: NormalizedResponse<Blob>,
  fileName?: string
): void {
  let name = ''
  const { headers, data } = res.axiosRes
  try {
    name = decodeURI(
      fileName || (headers['content-disposition'] || '').split('filename=')[1]
    )
    const blob = new Blob([data])
    saveAs(blob, name)
  } catch (e) {
    console.error(e)
  }
}

export function downloadFileByBlobUrl(
  res: NormalizedResponse<Blob>,
  fileName?: string
) {
  const { headers, data } = res.axiosRes
  const name = decodeURI(
    fileName || (headers['content-disposition'] || '').split('filename=')[1]
  )
  const blob = new Blob([data], {
    type: headers['content-type'] || 'application/vnd.ms-excel'
  })
  const blobUrl = URL.createObjectURL(blob)
  const dom = document.createElement('a')
  dom.style.display = 'none'
  dom.href = blobUrl
  dom.download = name
  document.body.append(dom)
  dom.click()
  document.body.removeChild(dom)
  URL.revokeObjectURL(blobUrl)
}

/**
 * 将基础筛选项与传入的其它选项进行合并，产生新的选项
 * @param base 原始项目 - 一个数组，包含有选项，类似 [{ prop: string, label: string }, ... ]
 * @param options 下拉列表中的选项，类似 { key: { prop }, ... }
 */
export const mergeOptionByKey = function (
  base: { [key: string]: any }[],
  options: { [key: string]: any } = {},
  key = 'prop'
) {
  base = cloneDeep(base)
  Object.keys(options).forEach((prop) => {
    const targetFilter = base.filter((item) => item[key] === prop)
    if (targetFilter[0]) {
      merge(targetFilter[0], options[prop])
    }
  })
  return base
}

/*
 * 通过对 fn 进行柯里化得到 el-table-column 的 formatter 函数
 * @param fn 普通的 formatter 转换函数
 * @return curriedFn el-table-column 的 formatter 函数
 */
export function curryTableColFormatter(fn: (val: any) => string) {
  return (row: any, col: any, cellVal: any): string => {
    return fn(cellVal)
  }
}

export function createLoading(message?: string) {
  return Loading.service({
    lock: true,
    text: message ?? '正在加载中....',
    spinner: 'el-icon-loading',
    background: 'rgba(0, 0, 0, 0.7)'
  })
}

export function wrapLoading<T extends object, K extends keyof T>(
  p: Promise<any>,
  context: T,
  prop: K
) {
  if (typeof context[prop] === 'boolean') {
    Object.assign(context, { [prop]: true }) // hack: 绕过 TS 检查
    p.finally(() => Object.assign(context, { [prop]: false }))
  }
  return p
}

export {
  redirectToLogin
} from './message'

/*
 * 通过字符串比较数值 a、b 的大小关系
 *  1 : a > b
 *  0 : a == b
 * -1 : a < b
 * @param a 比较项a
 * @param b 比较项b
 * @return -1 | 0 | 1
 */
export function strNumberCompare(a: string | number, b: string | number) {
  if (!a) a = 0
  if (!b) b = 0
  a = String(a)
  b = String(b)
  if (a === b) {
    return 0
  }
  if (a.length > b.length) {
    return 1
  }
  if (a.length < b.length) {
    return -1
  }

  const maxLength = Math.max(a.length, b.length)
  a.padStart(maxLength, '0')
  b.padStart(maxLength, '0')

  let i = 0
  while (i < maxLength) {
    if (a[i] < b[i]) {
      return -1
    } else if (a[i] > b[i]) {
      return 1
    } else {
      i++
    }
  }
}

/**
 * 在开发环境下，拼接 mock api 的请求地址
 * 若程序运行在生产环境中，将原样返回传入的请求地址，不再进行拼接
 *
 * @param path 相对路径, 从 Yapi 平台直接复制即可
 * @param mock 是否需要给路径增加 mock 所需的前缀
 */
export function _c(path: string, mock = false) {
  if (IS_PRODUCTION) {
    return path
  } else {
    return mock ? '/mock' + path : path
  }
}

/**
 * 将文件大小 B 转化成 KB MB GB
 */
const KB = 2 << 9
const MB = 2 << 19
const GB = 2 << 29
export function fileSizeFormatter (size?: number | string, fixed = 2) {
  const num = Number.parseInt(`${size}`)
  if (isNaN(num)) return ''
  if (num >= GB) {
    return (num / GB).toFixed(fixed) + ' GB'
  }
  if (num >= MB) {
    return (num / MB).toFixed(fixed) + ' MB'
  }
  if (num >= KB) {
    return (num / KB).toFixed(fixed) + ' KB'
  }
  return num + 'B'
}

/**
 * @desc 将字符串时间转换为浏览器所在时区的时间字符串
 * @dateString 时间字符串 ex: 2023-04-20 14:58:48
 * @timezone 时间字符串的时区 默认 8
 * @format 格式化
 */
export function convertToBrowserTimezone(dateString: string, timezone = 8, format = 'YYYY-MM-DD HH:mm:ss'): string {
  const inputDate = new Date(dateString)
  if (isNaN(inputDate.getTime())) {
    return '-' // 如果无法解析为有效的日期，则返回 null
  } else {
    const offset = inputDate.getTimezoneOffset() + (timezone * 60) // 计算偏移量
    return moment(dateString).subtract(offset, 'minutes').format(format)
  }
}

export function secondsToStr (temp: number) {
  const years = Math.floor(temp / 31536000)
  if (years) {
    return years + ' year' + numberEnding(years)
  }
  const days = Math.floor((temp %= 31536000) / 86400)
  if (days) {
    return days + ' day' + numberEnding(days)
  }
  const hours = Math.floor((temp %= 86400) / 3600)
  if (hours) {
    return hours + ' hour' + numberEnding(hours)
  }
  const minutes = Math.floor((temp %= 3600) / 60)
  if (minutes) {
    return minutes + ' minute' + numberEnding(minutes)
  }
  const seconds = temp % 60
  return seconds + ' second' + numberEnding(seconds)
  function numberEnding (number: number) {
    return (number > 1) ? 's' : ''
  }
}
export function formatSize(size: number) {
  if (size < 1024) {
    return size.toFixed(0) + ' bytes'
  } else if (size < 1024 * 1024) {
    return (size / 1024.0).toFixed(0) + ' KB'
  } else if (size < 1024 * 1024 * 1024) {
    return (size / 1024.0 / 1024.0).toFixed(1) + ' MB'
  } else {
    return (size / 1024.0 / 1024.0 / 1024.0).toFixed(1) + ' GB'
  }
}

// 计算字符串的md5值
export function getMd5(str: string) {
  return crypto.MD5(str).toString()
}
